import { addLog } from 'assets/components/feedback/Feedback';
import ModalForm from 'assets/components/form/modalForm/ModalForm';
import ThemeModalFormDefault from 'assets/components/form/modalForm/themes/Theme.ModalForm.Default';
import { getLocales } from 'assets/locales/Locale';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import { HttpStatus } from 'config/Api.Config';
import { first, isEmpty, omit, set } from 'lodash';
import claimJobSaveApi from 'models/claims/claimJob/save/Api.ClaimJob.Save';
import LossCode from 'models/claims/lossCode/Model.LossCode';
import ClaimJobStatusCode from 'models/enums/ClaimJobStatusCode';
import ClaimTypeDataPointInputType from 'models/enums/ClaimTypeDataPointInputType';
import { getVisibleDataPoints } from 'module/claims/views/claim/other/Claim.Job.DataPoints.Inputs';
import { useClaimJobEdit } from '../ClaimEdit.Job.Hooks';
import ClaimEditJobFormDataPoints from './tabs/ClaimEdit.Job.Form.DataPoints';
import ClaimEditJobFormInfo from './tabs/ClaimEdit.Job.Form.Info';
import ClaimEditJobFormItems from './tabs/ClaimEdit.Job.Form.Items';
import LineItem from 'models/enums/LineItem';

export type ClaimJobForm = Omit<Api.Core.ClaimJob.Save.IRequest, 'claimJobItems'> & {
  claimJobItems: Array<
    Utils.Unpacked<Api.Core.ClaimJob.Save.IRequest['claimJobItems']> & {
      templateId?: number;
      lossCodes?: LossCode[];
    }
  >;
};
export default function ClaimEditJobForm() {
  const { lang } = getLocales();
  const { userId } = useAuthStore();
  const { claimJob, claim, claimTypes, setAction, reloadClaim, mapClaimJobDataPoints, mapClaimJobItems } =
    useClaimJobEdit();
  const onlyClaimType = claimTypes && claimTypes.length === 1;

  return (
    <>
      <ModalForm<Api.Core.ClaimJob.Save.IRequest>
        subcomponents={{
          ...ThemeModalFormDefault,
          modal: {
            ...ThemeModalFormDefault.modal,
            page: {
              ...ThemeModalFormDefault.modal.page,
              style: {
                pageWrapper: {
                  width: '75%',
                },
              },
            },
          },
        }}
        title={lang.edit}
        onSubmit={async (data) => {
          const finalClaimJob = {
            ...data,
            claimTypeId: data.claimType?.id,
            claimTypeVersion: !claimJob?.id ? data.claimType?.version : data.claimTypeVersion,
            claimJobItems: data.claimJobItems?.map((it) =>
              it.jobItemTypeCode === LineItem.deductible
                ? {
                    ...it,
                    gstHst: 0,
                    provincialSalesTax: 0,
                    labourRate: 0,
                    hoursWorked: 0,
                  }
                : it
            ),
          };
          const res = await claimJobSaveApi(omit(finalClaimJob, 'claimType'));
          if (res.status === HttpStatus.ok) {
            addLog({ success: lang.saveSuccess });
            setAction('expanded');
          } else {
            addLog({ error: res.message });
          }
          reloadClaim();
        }}
        validation={(data, errors) => {
          if (!data.claimType?.id) set(errors, 'claimType', lang.required);
          if (
            getVisibleDataPoints(data.dataPoints).some(
              (it) => it.type !== ClaimTypeDataPointInputType.Boolean && it.isRequired && !it.value
            )
          )
            set(errors, 'dataPoints._objectError', lang.required);

          if (
            data.claimType?.id &&
            !isEmpty(data.claimJobItems) &&
            data.claimJobItems?.some((it) =>
              !it.claimJobItemPayee || it.jobItemTypeCode !== LineItem.deductible
                ? !it.lossCode || (it.gstHst ?? 0) > 1 || (it.provincialSalesTax ?? 0) > 1
                : (it.gstHst ?? 0) > 1 || (it.provincialSalesTax ?? 0) > 1
            )
          )
            set(errors, 'claimJobItems._objectError', lang.required);
        }}
        default={
          claimJob
            ? {
                ...claimJob?.toJSON(),
                dataPoints: claimJob?.dataPoints
                  ? claimJob?.dataPoints
                  : onlyClaimType &&
                    mapClaimJobDataPoints(
                      isEmpty(
                        first(claimTypes)?.allClaimTypeDataPoints?.filter(
                          (it) => it.version === claimJob?.claimTypeVersion
                        )
                      )
                        ? first(claimTypes)?.claimTypeDataPoints
                        : first(claimTypes)?.allClaimTypeDataPoints?.filter(
                            (it) => it.version === claimJob?.claimTypeVersion
                          ),
                      first(claimTypes)?.claimTypeAutomationRules
                    ),
              }
            : {
                ...claimJob?.toJSON(),
                claimTypeVersion: onlyClaimType ? first(claimTypes)?.version : null,
                claimTypeId: onlyClaimType ? first(claimTypes)?.id : null,
                cause: undefined,
                correction: undefined,
                customerComplaint: undefined,
                claimId: claim?.id,
                jobNumber: (claim?.claimJobs?.length || 0) + 1,
                statusCode: ClaimJobStatusCode.OPEN_ACTIVE_IN_PROGRESS,
                ownerId: userId,
                dataPoints:
                  onlyClaimType &&
                  mapClaimJobDataPoints(
                    first(claimTypes)?.claimTypeDataPoints,
                    first(claimTypes)?.claimTypeAutomationRules
                  ),
                claimJobItems: onlyClaimType && mapClaimJobItems(first(claimTypes)?.claimTypeJobItemTemplates),
              }
        }
        tabs={[
          { id: 'info', icon: 'fas-info', text: lang.info, errors: ({ form }) => form.errors?.claimType?.id },
          {
            id: 'dataPoints',
            icon: 'fas-table',
            text: lang.details,
            errors: ({ form }) => form.errors?.dataPoints?._objectError,
          },
          {
            id: 'jobItems',
            icon: 'fas-table',
            text: lang.jobItems,
            disabled: !!claimJob,
            errors: ({ form }) => form.errors?.claimJobItems?._objectError,
          },
        ]}
        onCancel={() => setAction('expanded')}
        render={({ activeTab }) =>
          activeTab.id == 'info' ? (
            <ClaimEditJobFormInfo />
          ) : activeTab.id == 'dataPoints' ? (
            <ClaimEditJobFormDataPoints />
          ) : activeTab.id == 'jobItems' ? (
            <ClaimEditJobFormItems />
          ) : null
        }
      />
    </>
  );
}
